import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { haptics } from "../haptics";
import { useToast } from "../../contexts/ToastContext";
import { PendingOut } from "../schemas";

type Action = "approve" | "deny" | "cancel";

export function usePendingMutation() {
  const qc = useQueryClient();
  const showToast = useToast();

  return useMutation({
    mutationFn: ({ token, action }: { token: string; action: Action }) =>
      api("POST", `/pending/${token}/${action}`),

    onMutate: async ({ token, action }) => {
      await qc.cancelQueries({ queryKey: ["pending"] });
      const snapshots = qc.getQueriesData<PendingOut[]>({ queryKey: ["pending"] });
      for (const [key, data] of snapshots) {
        if (data) qc.setQueryData(key, data.filter((p) => p.token !== token));
      }
      haptics[action === "approve" ? "success" : action === "deny" ? "warning" : "tap"]();
      return { snapshots, action };
    },

    onError: (err, _vars, ctx) => {
      ctx?.snapshots.forEach(([key, data]) => qc.setQueryData(key, data));
      showToast(err instanceof Error ? err.message : "Action failed", { type: "error" });
    },

    onSuccess: (_data, _vars, ctx) => {
      const label =
        ctx?.action === "approve" ? "Approved" : ctx?.action === "deny" ? "Denied" : "Cancelled";
      showToast(label, { type: ctx?.action === "approve" ? "success" : "info" });
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["pending"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
      void qc.invalidateQueries({ queryKey: ["group"] });
      void qc.invalidateQueries({ queryKey: ["balances"] });
      void qc.invalidateQueries({ queryKey: ["history"] });
    },
  });
}
